import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import BasePagination from 'components/atoms/base-pagination';
import BaseTableNew from 'components/atoms/base-table/new';
import { useGetProviderStatistics } from 'infrastructure/hooks/provider-dashboard/use-get-provider-statistics';

import s from './styles.module.scss';
import { formatNumber } from './utils';

import type { IBaseTableColumnProps } from 'components/atoms/base-table/types';
import type { IProviderRow } from './types';

interface IProviderDashboardProvidersTabProps {
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  dateRange: [Date, Date];
  clinicGuid: string[];
  parentClinicGuid: string[];
  providerGuid?: string;
}

export const ProviderDashboardProvidersTab: React.FC<
  IProviderDashboardProvidersTabProps
> = ({
  page,
  pageSize,
  onPageChange,
  dateRange,
  clinicGuid: included,
  parentClinicGuid,
  providerGuid,
}) => {
  const { t } = useTranslation();

  const startDate = dayjs(dateRange[0]).format('YYYY-MM-DD');
  const endDate = dayjs(dateRange[1]).format('YYYY-MM-DD');

  const { providerStatistics, isLoading, isFetching } =
    useGetProviderStatistics(
      {
        startDate,
        endDate,
        clinicGuid: included,
        parentClinicGuid,
        providerGuid,
        page: page - 1,
        pageSize,
      },
      Boolean(startDate && endDate),
    );

  const total = providerStatistics?.total ?? 0;

  const items = useMemo<IProviderRow[]>(
    () =>
      (providerStatistics?.items ?? []).map((item) => ({
        ...item,
        key: item.providerGuid,
      })),
    [providerStatistics?.items],
  );

  const providersColumns: IBaseTableColumnProps<any>[] = useMemo(() => {
    const tableHeaderWithSort = (label: string) => (
      <span className={s.providersTableHeader}>
        <span>{label}</span>
        {/* <ArrowSortIcon up /> */}
      </span>
    );
    return [
      {
        label: t('labels.providerName'),
        key: 'providerName',
        align: 'left',
        render: (_, record) => record.providerName,
      },
      {
        label: tableHeaderWithSort(t('labels.twoWaySync')),
        key: 'twoWaySync',
        align: 'center',
        render: (_, record) => formatNumber(record.twoWaySync),
      },
      {
        label: tableHeaderWithSort(t('labels.normal')),
        key: 'normal',
        align: 'center',
        render: (_, record) => formatNumber(record.normal),
      },
      {
        label: tableHeaderWithSort(t('labels.atRisk')),
        key: 'atRisk',
        align: 'center',
        render: (_, record) => formatNumber(record.atRisk),
      },
      {
        label: tableHeaderWithSort(t('labels.critical')),
        key: 'critical',
        align: 'center',
        render: (_, record) => formatNumber(record.critical),
      },
      {
        label: tableHeaderWithSort(t('labels.99453')),
        key: '99453',
        align: 'center',
        render: (_, record) => formatNumber(record.code99453),
      },
      {
        label: tableHeaderWithSort(t('labels.99454')),
        key: '99454',
        align: 'center',
        render: (_, record) => formatNumber(record.code99454),
      },
      {
        label: tableHeaderWithSort(t('labels.code99457')),
        key: 'code99457',
        align: 'center',
        render: (_, record) => formatNumber(record.code99457),
      },
      {
        label: tableHeaderWithSort(t('labels.code99458')),
        key: 'code99458',
        align: 'center',
        render: (_, record) => formatNumber(record.code99458),
      },
      {
        label: tableHeaderWithSort(t('labels.code99458_2')),
        key: 'code99458_2',
        align: 'center',
        render: (_, record) => formatNumber(record.code99458_2),
      },
      {
        label: tableHeaderWithSort(t('labels.code99458_3')),
        key: 'code99458_3',
        align: 'center',
        render: (_, record) => formatNumber(record.code99458_3),
      },
      {
        label: tableHeaderWithSort(t('labels.code99470')),
        key: 'code99470',
        align: 'center',
        render: (_, record) => formatNumber(record.code99470),
      },
      {
        label: tableHeaderWithSort(t('labels.code99445')),
        key: 'code99445',
        align: 'center',
        render: (_, record) => formatNumber(record.code99445),
      },
      {
        label: tableHeaderWithSort(t('labels.code99490')),
        key: 'code99490',
        align: 'center',
        render: (_, record) => formatNumber(record.code99490),
      },
      {
        label: tableHeaderWithSort(t('labels.code99439')),
        key: 'code99439',
        align: 'center',
        render: (_, record) => formatNumber(record.code99439),
      },
      {
        label: tableHeaderWithSort(t('labels.code99439_60')),
        key: 'code99439_60',
        align: 'center',
        render: (_, record) => formatNumber(record.code99439_60),
      },
      {
        label: tableHeaderWithSort(t('labels.code99489')),
        key: 'code99489',
        align: 'center',
        render: (_, record) =>
          record.code99489 === null ? '-' : formatNumber(record.code99489),
      },
      {
        label: tableHeaderWithSort(t('labels.totalBillingCodes')),
        key: 'totalBillingCodes',
        align: 'center',
        tableCollClassName: s.totalColumn,
        render: (_, record) => formatNumber(record.totalBillingCodes),
      },
    ];
  }, [t, s.providersTableHeader, s.totalColumn]);

  const rowsMeta = useMemo(() => {
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);
    return `Rows: ${start}-${end} of ${total}`;
  }, [total, page, pageSize]);

  return (
    <div className={s.providersSection}>
      <div className={s.providersHeaderRow}>
        <h2 className={s.providersHeading}>
          {t('labels.providersCount', { count: total })}
        </h2>
        <BasePagination
          page={page}
          total={total}
          pageSize={pageSize}
          onChange={onPageChange}
          align="end"
          className={s.providersHeaderPagination}
          showSizeChanger={false}
        />
      </div>
      <div className={s.providersTableWrap}>
        <BaseTableNew
          dataCy="provider-dashboard-providers-table"
          columns={providersColumns}
          items={items}
          rowKey="key"
          bordered
          loading={isLoading || isFetching}
          rowHoverable={false}
          changeOddRowColor
          rowClassName={s.reportRow}
        />
      </div>
      <div className={s.providersTableFooter}>
        <span className={s.providersTableMeta}>{rowsMeta}</span>
        <BasePagination
          page={page}
          total={total}
          pageSize={pageSize}
          onChange={onPageChange}
          align="center"
          className={s.providersFooterPagination}
          showSizeChanger={false}
        />
        <span className={s.providersFooterSpacer} aria-hidden />
      </div>
    </div>
  );
};
